import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Bought, Check, Expectation, FitPointMeta, NotesAccuracy, Outcome, Report as ReportT, ReportIn, Sitting, Step, Trait } from "../api/types";
import { HangTag, Tape, toast, vibrate } from "../components/Bits";
import { Icon } from "../components/Icons";
import { Pictogram } from "../components/Pictogram";
import { Sketch } from "../components/Sketch";
import { lvClass } from "../lib/fit";
import { useApp } from "../state";
import { isVneck } from "./Results";

/* ---------- Questions that are not fit points ---------- */
type Opt = { v: string; label: string; icon: string; sub?: string };
const o = (v: string, label: string, icon: string, sub?: string): Opt => ({ v, label, icon, sub });
const OTHER_SIZE = "__other";
const SOMETHING_ELSE = "Something else";

const Q: Record<string, { q: string; opts: Opt[] }> = {
  bought: { q: "", opts: [o("yes", "Yes", "bag"), o("not_yet", "Not yet", "clock", "We'll ask again later"), o("no", "Decided not to", "x")] },
  why_not: {
    q: "What made you pass?",
    opts: [o("The fit notes talked me out of it", "The fit notes talked me out of it", "ruler"), o("The price", "The price", "price"), o("Found something else", "Found something else", "eye"), o("Just wasn't feeling it", "Just wasn't feeling it", "meh")],
  },
  sitting: { q: "Sitting down for an hour?", opts: [o("comfortable", "Comfortable", "check"), o("fine", "Fine", "chair"), o("wanted_to_unbutton", "Wanted to unbutton", "unbutton")] },
  outcome: {
    q: "So what happened with it?",
    opts: [o("kept", "Kept it as is", "heart"), o("tailored", "Kept it and got it tailored", "scissors"), o("returned", "Returned it", "ret"), o("exchanged", "Exchanged for another size", "swap")],
  },
  return_reason: { q: "What was the main reason?", opts: [o("The fit", "The fit", "ruler"), o("The fabric", "The fabric", "fabric"), o("Color or look", "Color or look", "eye"), o("Changed my mind", "Changed my mind", "meh")] },
  expectation: { q: "Was it what you expected?", opts: [o("better", "Better than I expected", "up"), o("as_expected", "About what I expected", "check"), o("worse", "Worse than I expected", "down")] },
  accuracy: { q: "Did our fit notes match what you felt?", opts: [o("spot_on", "Spot on", "check"), o("mostly", "Mostly", "dot"), o("not_really", "Not really", "x")] },
};
const ALTER_ICON: Record<string, string> = {
  Hemmed: "scissors", "Took in the waist": "waist", "Tapered the leg": "taper", "Let out": "out", "Something else": "plus",
  "Shortened sleeves": "scissors", "Took in the sides": "in", "Took it in": "in", "Shortened straps": "scissors",
};
const STRIP: Record<string, string> = { bought: "Bought", size: "Size", why_not: "Why not", sitting: "Sitting", outcome: "Outcome", alterations: "Altered", return_reason: "Reason", expectation: "Expected", accuracy: "Our notes" };

type Answers = {
  bought?: Bought;
  why_not?: string;
  size?: string;
  sizeOther?: string;
  fit: Record<string, Step>;
  sitting?: Sitting;
  outcome?: Outcome;
  alterations?: string[];
  alterOther?: string;
  return_reason?: string;
  expectation?: Expectation;
  accuracy?: NotesAccuracy;
  free_text?: string;
};
type Key = Exclude<keyof Answers, "fit" | "sizeOther" | "alterOther" | "free_text">;

function svSteps(c: Check, fps: FitPointMeta[], a: Answers): string[] {
  const st = ["bought"];
  if (a.bought === "no") return [...st, "why_not"];
  if (a.bought !== "yes") return st;
  st.push("size", ...fps.map((f) => "fit:" + f.id));
  if (c.category === "pants" || c.category === "skirt") st.push("sitting");
  st.push("outcome");
  if (a.outcome === "tailored") st.push("alterations");
  if (a.outcome === "returned" || a.outcome === "exchanged") st.push("return_reason");
  st.push("expectation", "accuracy");
  return st;
}
function isDone(id: string, a: Answers): boolean {
  if (id.startsWith("fit:")) return a.fit[id.slice(4)] !== undefined;
  if (id === "size") return !!a.size && (a.size !== OTHER_SIZE || !!a.sizeOther);
  if (id === "alterations") return Array.isArray(a.alterations);
  return !!a[id as Key];
}
function answersFrom(r: ReportT, sizes: string[], tailoring: string[]): Answers {
  const known = (r.alterations ?? []).filter((x) => tailoring.includes(x));
  const other = (r.alterations ?? []).filter((x) => !tailoring.includes(x));
  return {
    bought: r.bought,
    why_not: r.why_not,
    size: r.size_label ? (sizes.includes(r.size_label) ? r.size_label : OTHER_SIZE) : undefined,
    sizeOther: r.size_label && !sizes.includes(r.size_label) ? r.size_label : undefined,
    fit: { ...(r.fit ?? {}) },
    sitting: r.sitting,
    outcome: r.outcome,
    alterations: r.outcome === "tailored" ? (other.length ? [...known, SOMETHING_ELSE] : known) : undefined,
    alterOther: other.join(", ") || undefined,
    return_reason: r.return_reason,
    expectation: r.expectation,
    accuracy: r.notes_accuracy,
    free_text: r.free_text,
  };
}
function toReport(a: Answers, steps: string[]): ReportIn {
  const has = (id: string) => steps.includes(id);
  const out: ReportIn = { bought: a.bought! };
  if (has("why_not") && a.why_not) out.why_not = a.why_not;
  if (has("size")) out.size_label = a.size === OTHER_SIZE ? a.sizeOther : a.size;
  if (has("size")) out.fit = Object.fromEntries(Object.entries(a.fit).filter(([k]) => has("fit:" + k)));
  if (has("sitting") && a.sitting) out.sitting = a.sitting;
  if (has("outcome") && a.outcome) out.outcome = a.outcome;
  if (has("alterations")) {
    const list = (a.alterations ?? []).filter((x) => x !== SOMETHING_ELSE);
    if ((a.alterations ?? []).includes(SOMETHING_ELSE)) list.push(a.alterOther?.trim() || SOMETHING_ELSE);
    out.alterations = list;
  }
  if (has("return_reason") && a.return_reason) out.return_reason = a.return_reason;
  if (has("expectation") && a.expectation) out.expectation = a.expectation;
  if (has("accuracy") && a.accuracy) out.notes_accuracy = a.accuracy;
  if (a.free_text?.trim()) out.free_text = a.free_text.trim();
  return out;
}

/** A report with more than the quick "did you buy it" answer from History. */
const reportDone = (r: ReportIn) => (r.bought === "yes" ? !!r.size_label : r.bought === "no" ? !!r.why_not : false);

export function Report() {
  const { id } = useParams();
  const { meta, newBoard } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [check, setCheck] = useState<Check | null>(null);
  const [loadErr, setLoadErr] = useState("");
  const [a, setA] = useState<Answers>({ fit: {} });
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<string | null>(null);
  const [zoneSel, setZoneSel] = useState<string | null>(null);
  const [zoneEdit, setZoneEdit] = useState(false);
  const [tmp, setTmp] = useState<string[] | null>(null);
  const [just, setJust] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [sew, setSew] = useState(false);
  const [traits, setTraits] = useState<Trait[] | null>(null);

  useEffect(() => {
    if (!id) return;
    api.check(id).then(
      ({ check }) => {
        setCheck(check);
        // Already answered Bought or Decided not to from History: start at the next question.
        const b = check.report?.bought;
        if (check.report && !reportDone(check.report) && b !== "not_yet") setA({ fit: {}, bought: b });
      },
      (e) => setLoadErr(errorMessage(e)),
    );
  }, [id]);
  const showLearned = !!check?.report && reportDone(check.report) && !editing;
  useEffect(() => {
    if (showLearned) api.traits().then((x) => setTraits(x.traits), () => setTraits([]));
  }, [showLearned]);

  const goBack = () => (loc.key !== "default" ? nav(-1) : nav("/"));

  if (loadErr)
    return (
      <section className="view enter">
        <div className="banner bad">
          <b>We couldn't open this fit report.</b>
          <p className="small">{loadErr}</p>
        </div>
      </section>
    );
  if (!check)
    return (
      <section className="view">
        <p className="eyebrow">Loading…</p>
      </section>
    );

  const cm = meta.categories[check.category];
  const fps = cm?.fit_points ?? [];
  const r = check.result;
  const sizeOpts = r?.focus_sizes.length ? r.focus_sizes : r?.sizes ?? check.candidate.sizes.map((s) => s.label);
  const allSizes = r ? [...new Set([...r.sizes, ...(r.ranges ?? []).flatMap((x) => x.result.sizes)])] : check.candidate.sizes.map((s) => s.label);

  /* ---------- After a report: what we learned ---------- */
  if (showLearned && check.report) {
    return (
      <LearnedView
        check={check}
        fps={fps}
        traits={traits}
        sew={sew}
        onEdit={() => {
          setA(check.report!.bought === "not_yet" ? { fit: {} } : answersFrom(check.report!, sizeOpts, cm?.tailoring_options ?? []));
          setEditing(true);
          setEdit(null);
        }}
        onNew={newBoard}
        onProfile={() => nav("/profile")}
      />
    );
  }

  const steps = svSteps(check, fps, a);
  const fitIds = steps.filter((s) => s.startsWith("fit:")).map((s) => s.slice(4));
  const done = steps.filter((s) => isDone(s, a)).length;
  const strip = steps.filter((s) => !s.startsWith("fit:") && isDone(s, a) && edit !== s);
  const nextId = edit ?? steps.find((s) => !isDone(s, a) && !s.startsWith("fit:")) ?? null;
  const zonesLeft = fitIds.filter((f) => a.fit[f] === undefined);
  const orderOk = (s: string) => !fitIds.length || steps.indexOf(s) < steps.indexOf("fit:" + fitIds[0]);
  const askNonZone = !!nextId && (!!edit || orderOk(nextId) || !zonesLeft.length);
  const qBefore = askNonZone && orderOk(nextId!);

  const pick = async (qid: string, v: string) => {
    vibrate();
    setJust(qid + ":" + v);
    if (qid === "bought") {
      const b = v as Bought;
      setA({ fit: {}, bought: b });
      if (b === "not_yet") {
        try {
          await api.report(check.id, { bought: "not_yet" });
          toast("Okay, we'll ask again later");
          goBack();
        } catch (e) {
          setSaveErr(errorMessage(e));
        }
        return;
      }
    } else if (qid === "outcome") {
      setA((x) => ({ ...x, outcome: v as Outcome, alterations: undefined, return_reason: undefined }));
    } else if (qid === "size") {
      setA((x) => ({ ...x, size: v, sizeOther: v === OTHER_SIZE ? x.sizeOther : undefined }));
    } else {
      setA((x) => ({ ...x, [qid]: v }));
    }
    if (edit === qid && !(qid === "size" && v === OTHER_SIZE)) setEdit(null);
  };
  const pickFit = (fpId: string, step: Step) => {
    vibrate();
    setJust("fit:" + fpId + ":" + step);
    setA((x) => ({ ...x, fit: { ...x.fit, [fpId]: step } }));
    setZoneEdit(false);
    setZoneSel(null);
  };
  const save = async () => {
    setSaving(true);
    setSaveErr("");
    try {
      const { check: c } = await api.report(check.id, toReport(a, steps));
      setCheck(c);
      setEditing(false);
      setSew(true);
      window.scrollTo(0, 0);
      toast("✓ Fit report saved");
    } catch (e) {
      setSaveErr(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const qCard = (qid: string) => {
    if (qid === "size") {
      const extra = allSizes.filter((s) => !sizeOpts.includes(s));
      return (
        <div className="bcard qcard" key="q-size">
          <span className="tag">
            <b>#</b>Size
          </span>
          <h2>Which size did you get?</h2>
          <div className="row">
            {[...sizeOpts, OTHER_SIZE].map((s) => (
              <button key={s} className={"chip size" + (just === "size:" + s ? " just" : "")} type="button" aria-pressed={a.size === s} onClick={() => void pick("size", s)}>
                {s === OTHER_SIZE ? "A different size" : s}
              </button>
            ))}
          </div>
          {a.size === OTHER_SIZE && (
            <form
              className="stack"
              noValidate
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                const el = document.getElementById("size-other") as HTMLInputElement | null;
                const v = el?.value.trim() ?? "";
                if (!v) return el?.focus();
                setA((x) => ({ ...x, sizeOther: v }));
                setEdit(null);
              }}
            >
              {extra.length > 0 && (
                <div className="row">
                  {extra.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="chip sm"
                      onClick={() => {
                        setA((x) => ({ ...x, sizeOther: s }));
                        setEdit(null);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <label className="aside" htmlFor="size-other">
                Which size did you get? Write it the way the label does.
              </label>
              <div className="seam">
                <input id="size-other" autoComplete="off" placeholder="e.g. 27 Petite, EU 38, XXS" defaultValue={a.sizeOther ?? ""} />
                <button className="btn primary" type="submit">
                  Use this size
                </button>
              </div>
            </form>
          )}
        </div>
      );
    }
    if (qid === "alterations") {
      const cur = tmp ?? a.alterations ?? [];
      const opts = cm?.tailoring_options ?? [];
      return (
        <div className="bcard qcard" key="q-alt">
          <span className="tag">
            <b>?</b>Altered
          </span>
          <h2>What did you have altered?</h2>
          <p className="small muted">Pick any that apply.</p>
          <div className="tiles wide">
            {opts.map((t) => {
              const on = cur.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  className={"tile" + (just === "alt:" + t ? " just" : "")}
                  aria-pressed={on}
                  onClick={() => {
                    vibrate();
                    setJust("alt:" + t);
                    setTmp(on ? cur.filter((x) => x !== t) : [...cur, t]);
                  }}
                >
                  <span className="pic ic">
                    <Icon name={ALTER_ICON[t] ?? "scissors"} />
                  </span>
                  {t}
                </button>
              );
            })}
          </div>
          {cur.includes(SOMETHING_ELSE) && (
            <label className="stack" htmlFor="tailor-other">
              <span className="aside">What else did you have altered?</span>
              <span className="seam">
                <input id="tailor-other" autoComplete="off" placeholder="e.g. moved the button, shortened the straps" value={a.alterOther ?? ""} onChange={(e) => setA((x) => ({ ...x, alterOther: e.target.value }))} />
              </span>
            </label>
          )}
          <button
            className="btn"
            type="button"
            onClick={() => {
              setA((x) => ({ ...x, alterations: cur }));
              setTmp(null);
              setEdit(null);
            }}
          >
            {cur.length ? "Done" : "Nothing altered, skip"}
          </button>
        </div>
      );
    }
    const def = Q[qid];
    const title = qid === "bought" ? `Did you end up buying the ${check.candidate.title.replace(/^the\s+/i, "")}?` : def.q;
    const cur = a[qid as Key];
    return (
      <div className="bcard qcard" key={"q-" + qid}>
        <span className="tag">
          <b>?</b>
          {STRIP[qid] ?? qid}
        </span>
        <h2>{title}</h2>
        <div className="tiles wide">
          {def.opts.map((op) => (
            <button key={op.v} type="button" className={"tile" + (just === qid + ":" + op.v ? " just" : "")} aria-pressed={cur === op.v} onClick={() => void pick(qid, op.v)}>
              <span className="pic ic">
                <Icon name={op.icon} />
              </span>
              {op.label}
              {op.sub && <small>{op.sub}</small>}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const stripValue = (s: string): string => {
    if (s === "size") return a.size === OTHER_SIZE ? a.sizeOther ?? "" : a.size ?? "";
    if (s === "alterations") {
      const list = (a.alterations ?? []).filter((x) => x !== SOMETHING_ELSE);
      if ((a.alterations ?? []).includes(SOMETHING_ELSE)) list.push(a.alterOther?.trim() || SOMETHING_ELSE);
      return list.join(", ") || "Nothing";
    }
    const v = a[s as Key] as string | undefined;
    return Q[s]?.opts.find((x) => x.v === v)?.label ?? v ?? "";
  };

  // The garment stage for fit-point questions.
  let stage = null;
  if (fitIds.length && a.size && !qBefore) {
    const curZ = zoneSel && fitIds.includes(zoneSel) && (a.fit[zoneSel] === undefined || zoneEdit) ? zoneSel : zonesLeft[0] ?? null;
    const fpCur = fps.find((f) => f.id === curZ);
    const pins = fitIds.map((fid, i) => {
      const f = fps.find((x) => x.id === fid)!;
      const ans = a.fit[fid];
      return {
        zone: f.zone,
        cls: fid === curZ ? "cur" : ans !== undefined ? lvClass(ans) : "todo",
        label: ans !== undefined && fid !== curZ ? "✓" : String(i + 1),
        name: f.label,
      };
    });
    stage = (
      <div className="bcard">
        <span className={"tag" + (zonesLeft.length ? "" : " done")}>
          <b>{zonesLeft.length ? "✱" : "✓"}</b>
          {zonesLeft.length ? `Spot ${fitIds.length - zonesLeft.length + 1} of ${fitIds.length} · it lights up` : "Every spot answered"}
        </span>
        <div className="stage">
          <Sketch
            cat={check.category}
            pins={pins}
            vneck={isVneck(check.candidate.labels)}
            active={fpCur?.zone ?? null}
            onPick={(z) => {
              const f = fps.find((x) => x.zone === z);
              if (!f) return;
              setZoneSel(f.id);
              setZoneEdit(true);
              setEdit(null);
            }}
          />
          {fpCur ? (
            <div className="qcard" key={fpCur.id}>
              <span className="eyebrow">{fpCur.label}</span>
              <h2>{fpCur.question}</h2>
              <div className="tiles">
                {fpCur.tiles.map((t) => (
                  <button
                    key={t.step + t.label}
                    type="button"
                    className={"tile" + (just === `fit:${fpCur.id}:${t.step}` ? " just" : "")}
                    aria-pressed={a.fit[fpCur.id] === t.step}
                    onClick={() => pickFit(fpCur.id, t.step)}
                  >
                    <Pictogram zone={fpCur.zone} step={t.step} />
                    {t.label}
                    {t.sub && <small>{t.sub}</small>}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="zinfo done">
              <h3>All spots done.</h3>
              <p className="small muted">Tap any spot on the garment to change an answer.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const sizeShown = a.size === OTHER_SIZE ? a.sizeOther : a.size;
  return (
    <section className="view enter">
      <HangTag p={check.candidate} cat={check.category} size={sizeShown ?? null} />
      <h1>
        <span className="ph">
          What <em>happened</em>
        </span>{" "}
        <span className="ph">with it?</span>
      </h1>
      <p className="aside">
        Just tap what you felt. No typing, and <mark>your next notes get sharper.</mark>
      </p>
      <div className="stack">
        <Tape fill={Math.round((done / steps.length) * 100)} />
        <span className="eyebrow" aria-live="polite">
          <span className="num">{done}</span> of {steps.length} answered
        </span>
      </div>

      {strip.length > 0 && (
        <div className="answered-strip">
          {strip.map((s) => (
            <button key={s} className="chip sm lite" type="button" onClick={() => setEdit(s)}>
              {STRIP[s] ?? s}: <b>{stripValue(s)}</b> <span className="edit-pen" aria-hidden="true">✎</span>
            </button>
          ))}
        </div>
      )}

      {qBefore && nextId && qCard(nextId)}
      {stage}
      {askNonZone && !qBefore && nextId && qCard(nextId)}
      {!nextId && !zonesLeft.length && a.bought && a.bought !== "not_yet" && (
        <>
          <div className="bcard">
            <span className="tag">
              <b>+</b>Anything else? Optional
            </span>
            <label className="aside" htmlFor="rp-notes">
              Anything else uncomfortable or off about it? Say it however you like.
            </label>
            <textarea
              id="rp-notes"
              className="notes"
              rows={3}
              maxLength={2000}
              placeholder="e.g. the pockets sit too low, the waistband rolls when I sit, the fabric is itchy"
              value={a.free_text ?? ""}
              onChange={(e) => setA((x) => ({ ...x, free_text: e.target.value }))}
            />
          </div>
          <button className="btn primary" type="button" disabled={saving} onClick={() => void save()}>
            Save my fit report
          </button>
        </>
      )}
      {saveErr && (
        <p className="hint warn" role="alert">
          {saveErr}
        </p>
      )}
    </section>
  );
}

function LearnedView(props: { check: Check; fps: FitPointMeta[]; traits: Trait[] | null; sew: boolean; onEdit: () => void; onNew: () => void; onProfile: () => void }) {
  const { check, fps, traits, sew, onEdit, onNew, onProfile } = props;
  const rep = check.report!;
  const size = rep.size_label ?? null;
  const notes = size ? check.result?.by_size[size] : undefined;
  const fit = rep.fit ?? {};
  const keys = fps.filter((f) => fit[f.id] !== undefined);
  const matchedOf = (fid: string): boolean | null => {
    const n = notes?.fit_points.find((x) => x.fit_point === fid);
    if (!n) return null;
    const felt = fit[fid];
    if (n.range) return felt >= Math.min(...n.range) && felt <= Math.max(...n.range);
    return felt === n.step;
  };
  const matched = keys.filter((f) => matchedOf(f.id)).length;
  const pins = fps.map((f) => {
    const felt = fit[f.id];
    const m = matchedOf(f.id);
    return { zone: f.zone, cls: felt !== undefined ? lvClass(felt) : "todo", label: felt === undefined ? "·" : m === false ? "!" : "✓", name: f.label };
  });

  return (
    <section className="view enter">
      <HangTag p={check.candidate} cat={check.category} size={size} />
      <div className={"carelabel" + (sew ? " fx-sew" : "")} id="label">
        <i className="th t1" />
        <i className="th t2" />
        <i className="th t3" />
        <i className="th t4" />
        {rep.bought === "no" ? (
          <>
            <span className="eyebrow">Sewn into your profile</span>
            <h2>
              Skipped it. <em>Good call</em> if it wasn't right.
            </h2>
            {check.reference.favorite_id && (
              <p className="small muted">We kept your {check.reference.product.brand} as a piece that fits you, so next time it's one tap.</p>
            )}
          </>
        ) : rep.bought === "not_yet" ? (
          <>
            <span className="eyebrow">Still deciding</span>
            <h2>We'll ask again later.</h2>
            <p className="small muted">When it arrives, tell us how it fits and your next notes get sharper.</p>
          </>
        ) : (
          <>
            <span className="eyebrow">Fit report sewn into your profile</span>
            {notes && keys.length ? (
              <h2>
                Our notes matched{" "}
                <span className="hl">
                  {matched} of {keys.length}
                </span>{" "}
                spots in size {size}.
              </h2>
            ) : (
              <h2>Thanks. We've noted how size {size} felt.</h2>
            )}
            <p className="small muted">{notes && matched === keys.length ? "Your next notes get a confidence boost." : "We'll correct for the misses next time, so your next notes get sharper."}</p>
            <div className="stage">
              <Sketch cat={check.category} pins={pins} vneck={isVneck(check.candidate.labels)} animate={sew} label="How each spot felt" />
              <div className="zinfo">
                <span className="eyebrow">What we learned</span>
                {traits === null ? (
                  <p className="small muted">Reading your profile…</p>
                ) : traits.length ? (
                  <ul className="learn">
                    {traits.slice(0, 5).map((t) => (
                      <li key={t.code}>
                        <b>{t.label}</b>
                        <span>{t.explanation}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="small">Nothing surprising this time. Every report still makes the next notes more sure.</p>
                )}
              </div>
            </div>
          </>
        )}
        {rep.free_text && <p className="aside">You noted: “{rep.free_text}”</p>}
        <div className="row">
          <button className="linkish" type="button" onClick={onEdit}>
            {rep.bought === "not_yet" ? "Answer now" : "Change my answers"}
          </button>
        </div>
      </div>
      <button className="btn primary" type="button" onClick={onNew}>
        Check another item
      </button>
      <button className="btn" type="button" onClick={onProfile}>
        See your fit profile
      </button>
    </section>
  );
}
